import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Send, User, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTicket(data);
      }
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: newMessage })
      });

      if (res.ok) {
        const msg = await res.json();
        setTicket({
          ...ticket,
          messages: [...(ticket.messages || []), msg]
        });
        setNewMessage('');
        toast.success('Message sent!');
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Something went wrong');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
      in_progress: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
      resolved: 'text-green-400 bg-green-400/10 border-green-400/20',
      closed: 'text-gray-400 bg-gray-400/10 border-gray-400/20'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-400 bg-gray-400/10',
      medium: 'text-yellow-400 bg-yellow-400/10',
      high: 'text-orange-400 bg-orange-400/10',
      critical: 'text-red-400 bg-red-400/10'
    };
    return colors[priority] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-bold text-white mb-4">Ticket Not Found</h2>
        <Link to="/tickets" className="text-blue-400 hover:underline">
          Back to Tickets
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="ticket-detail-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <Link 
          to="/tickets"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors mb-4 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tickets
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">{ticket.subject}</h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(ticket.status)}`}>
                {ticket.status.replace('_', ' ')}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority} priority
              </span>
              <span className="px-3 py-1 rounded-full text-sm bg-blue-500/10 text-blue-400 border border-blue-500/20">
                {ticket.category}
              </span>
            </div>
          </div>
          <div className="text-sm text-gray-400 flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            Created {formatDate(ticket.created_at)}
          </div>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-xl border"
            style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
          >
            <h2 className="text-lg font-semibold text-white mb-4">Description</h2>
            <p className="text-gray-400 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
          </motion.div>

          {/* Messages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 rounded-xl border"
            style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
          >
            <h2 className="text-lg font-semibold text-white mb-4">Conversation</h2>

            {/* Message List */}
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {(!ticket.messages || ticket.messages.length === 0) ? (
                <p className="text-gray-500 text-center py-8">No messages yet. Start the conversation below.</p>
              ) : (
                ticket.messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex gap-3 ${msg.is_staff ? '' : 'flex-row-reverse'}`}
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border
                      ${msg.is_staff ? 'bg-blue-500/20 border-blue-500/20' : 'bg-blue-500/10 border-blue-500/10'}
                    `}>
                      {msg.is_staff ? (
                        <Bot className="w-5 h-5 text-blue-400" />
                      ) : (
                        <User className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div className={`
                      max-w-[80%] p-4 rounded-xl
                      ${msg.is_staff 
                        ? 'bg-[#080B14]/40 text-gray-300 rounded-tl-md border border-blue-500/5' 
                        : 'bg-blue-500/10 text-gray-200 rounded-tr-md border border-blue-500/20'
                      }
                    `}>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                      <span className="text-xs text-gray-500 mt-2 block">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            {ticket.status !== 'closed' && (
              <form onSubmit={sendMessage} className="flex gap-3">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={2}
                  className="flex-1 bg-[#080B14]/40 border-blue-500/15 text-white resize-none placeholder:text-gray-500 focus:border-blue-500/50"
                  data-testid="ticket-message-input"
                />
                <Button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold px-6 border-none shadow-[0_2px_10px_rgba(59,130,246,0.2)]"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          <div 
            className="p-6 rounded-xl border"
            style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
          >
            <h3 className="text-lg font-semibold text-white mb-4">Ticket Details</h3>
            <div className="space-y-4">
              <div>
                <span className="text-gray-500 text-sm">Ticket ID</span>
                <p className="text-white font-mono text-sm">{ticket.ticket_id}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Category</span>
                <p className="text-white">{ticket.category}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Priority</span>
                <p className={getPriorityColor(ticket.priority).split(' ')[0]}>
                  {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Last Updated</span>
                <p className="text-white text-sm">{formatDate(ticket.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Help Box */}
          <div 
            className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 shadow-lg"
          >
            <h3 className="text-white font-semibold mb-2">Need Urgent Help?</h3>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              For critical issues, contact our 24/7 support hotline.
            </p>
            <p className="text-blue-400 font-mono font-medium">8436661595 / 7384331647/ 8617720844</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
